import React from "react";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { I18nextProvider } from "react-i18next";
import { store, persistor } from "./core/store";
import { queryClient } from "@/core/api-client";
// Сайд-эффект: setOnUnauthenticated. SessionBootstrap его не тянет.
import "@/core/session/bootstrap";
import { SessionBootstrap } from "@/core/session/SessionBootstrap";
import { useDynamicTheme } from "@/theme/useDynamicTheme";
import { AppProvider } from "@/context/app";
import { CategoryScrollProvider } from "@/context/CategoryScrollContext";
import { SnackbarProvider } from "@/context/SnackbarContext";
import { AppInitializer } from "@/components/AppInitializer";
import { LoadingAnimation } from "@/components/molecules/LoadingAnimation";
import i18n from "@/localize/index";
import { AppRouter } from "@/router";
import "./App.css";

// Default theme for PersistGate loading (before dynamic theme is available)
const defaultTheme = createTheme({
  palette: {
    primary: {
      main: "#171818",
    },
    secondary: {
      main: "#9c27b0",
    },
  },
});

// Theme Provider Wrapper Component
const ThemeProviderWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const dynamicTheme = useDynamicTheme();

  return <ThemeProvider theme={dynamicTheme}>{children}</ThemeProvider>;
};

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <PersistGate
        loading={
          <ThemeProvider theme={defaultTheme}>
            <LoadingAnimation
              size="large"
              color="primary"
              backdrop={true}
              loading={true}
            />
          </ThemeProvider>
        }
        persistor={persistor}
      >
        <QueryClientProvider client={queryClient}>
          <I18nextProvider i18n={i18n}>
            <ThemeProviderWrapper>
              <CssBaseline />
              <AppProvider>
                <CategoryScrollProvider>
                  <SnackbarProvider>
                    <SessionBootstrap>
                      <AppInitializer>
                        <AppRouter />
                      </AppInitializer>
                    </SessionBootstrap>
                  </SnackbarProvider>
                </CategoryScrollProvider>
              </AppProvider>
            </ThemeProviderWrapper>
          </I18nextProvider>
        </QueryClientProvider>
      </PersistGate>
    </Provider>
  );
};

export default App;
